import type { RecipientFeedbackCollectionResponse } from '@/lib/types';
import { useCanonicalFeedback } from './useCanonicalFeedback';

type RecipientRow = RecipientFeedbackCollectionResponse['items'][number];
export type RecipientFeedbackDescriptor = Extract<RecipientRow, { row_kind: 'feedback' }>;
export type RecipientRecapDescriptor = Extract<RecipientRow, { row_kind: 'recap' }>;
const feedbackRows = (data: ReturnType<typeof useCanonicalFeedback>['data']) => data?.view === 'recipient'
  ? data.items.filter((row): row is RecipientFeedbackDescriptor => row.row_kind === 'feedback') : undefined;

/** Summary reads never inherit table filters, cursors, or reading side effects.
 * Totals are the server's filtered totals for each explicit kind/purpose, not
 * the mixed authorized_total or the number of descriptors on the first page. */
export function useCanonicalRecipientSummary(playerId?: string | null, enabled = true) {
  const player = playerId ? { player_id: playerId } : {};
  const recent = useCanonicalFeedback('recipient', { ...player, kind: 'feedback', attention: 'all', sort: 'recent', limit: 20 }, enabled);
  const attention = useCanonicalFeedback('recipient', { ...player, kind: 'feedback', attention: 'attention', sort: 'recent', limit: 20 }, enabled);
  const recaps = useCanonicalFeedback('recipient', { ...player, kind: 'recap', attention: 'all', sort: 'recent', limit: 20 }, enabled);
  const recentFeedback = feedbackRows(recent.data);
  const attentionFeedback = feedbackRows(attention.data);
  const recapData = recaps.data?.view === 'recipient' ? recaps.data : undefined;
  return {
    recent, attention, recaps,
    recentFeedback,
    nextFeedback: attentionFeedback ? attentionFeedback[0] ?? null : undefined,
    latestRecap: recapData ? recapData.items.find((row): row is RecipientRecapDescriptor => row.row_kind === 'recap') ?? null : undefined,
    feedbackTotal: recent.data?.view === 'recipient' ? recent.data.total : undefined,
    attentionFeedbackTotal: attention.data?.view === 'recipient' ? attention.data.total : undefined,
    recapTotal: recapData?.total,
  };
}
